import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon
} from "@heroicons/react/20/solid";
import { DateTime } from "luxon";
import { useTeamSchedule } from "./TeamScheduleContext.js";
import { Button } from "@swivu/shared-ui/src/components/Button.js";
import TeamScheduleDropdown from "./TeamScheduleDropdown.js";
import { Input } from "@swivu/shared-ui/src/components/Input.js";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { cn, useIsGummy } from "@swivu/shared-ui";

const getDisplayMonth = (dates: Date[]) => {
  const start = dates.at(0);
  const end = dates.at(-1);
  if (!start || !end) return "No dates selected";

  const startDt = DateTime.fromJSDate(start);
  const endDt = DateTime.fromJSDate(end);

  if (startDt.hasSame(endDt, "month")) {
    return startDt.toFormat("MMM yyyy");
  }
  return `${startDt.toFormat("MMM yyyy")} - ${endDt.toFormat("MMM yyyy")}`;
};

const GUMMY_NAV_PILL = cn(
  "inline-flex items-center justify-center text-mono-800 dark:text-mono-200",
  "bg-tile-neutral font-display font-black",
  "transition-transform duration-150 gummy-lift active:translate-y-0 active:scale-[0.96]",
  "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40",
  "shadow-raised",
  "motion-reduce:transform-none"
);

const DateRangeSelector = () => {
  const { dateRange, changeDays, setSearch, search, resetDays, timezone } =
    useTeamSchedule();
  const isGummy = useIsGummy();
  const isTodayTheStartDate = DateTime.fromJSDate(dateRange[0])
    .setZone(timezone)
    .startOf("day")
    .plus({ days: 2 })
    .hasSame(DateTime.now().setZone(timezone).startOf("day"), "day");
  return (
    <div
      className={`flex flex-col justify-between gap-2 px-2 py-2 sm:flex-row sm:items-center`}
    >
      <div className={`flex items-center justify-between`}>
        <h2
          className={cn(
            "mobile-pref-section-title",
            isGummy
              ? "text-xl font-display font-black tracking-tight"
              : "text-lg font-bold"
          )}
        >
          {getDisplayMonth(dateRange)}
        </h2>
        <div className={`flex items-center gap-2`}>
          <div className="agenda-date-range-selector sm:hidden">
            {isGummy ? (
              <div className="inline-flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => changeDays("-")}
                  className={cn(
                    "mobile-pref-nav-button h-12 w-12 rounded-surface-md",
                    GUMMY_NAV_PILL
                  )}
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon
                    className="mobile-pref-control-icon mobile-pref-detail-icon size-6"
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  onClick={() => changeDays("+")}
                  className={cn(
                    "mobile-pref-nav-button h-12 w-12 rounded-surface-md",
                    GUMMY_NAV_PILL
                  )}
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon
                    className="mobile-pref-control-icon mobile-pref-detail-icon size-6"
                    aria-hidden="true"
                  />
                </button>
              </div>
            ) : (
              <div className="isolate inline-flex rounded-md ">
                <Button
                  onClick={resetDays}
                  type="button"
                  size="small"
                  className={cn(
                    `mobile-pref-inline-button dark:bg-mono-950 dark:ring-mono-700/50 text-mono-800 dark:text-mono-400 ring-mono-200 hover:bg-mono-50 dark:border-mono-700/50 dark:hover:border-mono-500 mr-2 hidden h-8 border bg-white py-0.5 dark:hover:bg-transparent dark:hover:text-white md:block`,
                    {
                      " dark:border-mono-500": !isTodayTheStartDate,
                      " dark:border-mono-700/50": isTodayTheStartDate
                    }
                  )}
                >
                  Today
                </Button>
                <Button
                  type="button"
                  size="small"
                  className="mobile-pref-nav-button dark:bg-mono-950 dark:ring-mono-700/50 text-mono-800 dark:text-mono-400 ring-mono-200 hover:bg-mono-50 dark:hover:ring-mono-500 relative inline-flex items-center rounded-l-md rounded-r-none bg-white px-1.5 py-0.5 ring-0 ring-inset hover:z-10 focus:z-10 dark:hover:bg-transparent dark:hover:text-white"
                  onClick={() => {
                    changeDays("-");
                  }}
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon
                    className="mobile-pref-control-icon mobile-pref-detail-icon"
                    aria-hidden="true"
                  />
                </Button>
                <Button
                  size="small"
                  className="mobile-pref-nav-button dark:hover:ring-mono-500 dark:bg-mono-950 dark:ring-mono-700/50 text-mono-800 dark:text-mono-400 ring-mono-200 hover:bg-mono-50 relative -ml-px inline-flex items-center rounded-l-none rounded-r-md bg-white px-1.5 py-0.5 ring-0 ring-inset focus:z-10 dark:hover:bg-transparent dark:hover:text-white"
                  onClick={() => {
                    changeDays("+");
                  }}
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon
                    className="mobile-pref-control-icon mobile-pref-detail-icon"
                    aria-hidden="true"
                  />
                </Button>
              </div>
            )}
          </div>
          <div className={`sm:hidden`}>
            <TeamScheduleDropdown />
          </div>
        </div>
      </div>
      <div className={`flex items-center justify-end gap-3`}>
        {isGummy ? (
          <button
            onClick={resetDays}
            type="button"
            className={cn(
              "mobile-pref-inline-button block h-12 rounded-full px-5 text-sm uppercase tracking-widest",
              GUMMY_NAV_PILL,
              isTodayTheStartDate && "opacity-50"
            )}
          >
            Today
          </button>
        ) : (
          <Button
            onClick={resetDays}
            type="button"
            size="small"
            className={cn(
              `mobile-pref-inline-button dark:bg-mono-950 dark:ring-mono-700/50 text-mono-800 dark:text-mono-400 ring-mono-200 hover:bg-mono-50 dark:border-mono-700/50 dark:hover:border-mono-500 block h-8 border bg-white px-4 dark:hover:bg-transparent dark:hover:text-white`,
              {
                " dark:border-mono-500 ": !isTodayTheStartDate,
                " dark:border-mono-700/50 ": isTodayTheStartDate
              }
            )}
          >
            Today
          </Button>
        )}
        <div className="agenda-date-range-selector hidden sm:block">
          {isGummy ? (
            <span className="inline-flex items-center gap-2">
              <button
                type="button"
                onClick={() => changeDays("-")}
                className={cn(
                  "mobile-pref-nav-button h-12 w-12 rounded-surface-md",
                  GUMMY_NAV_PILL
                )}
              >
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon
                  className="mobile-pref-control-icon mobile-pref-detail-icon size-6"
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                onClick={() => changeDays("+")}
                className={cn(
                  "mobile-pref-nav-button h-12 w-12 rounded-surface-md",
                  GUMMY_NAV_PILL
                )}
              >
                <span className="sr-only">Next</span>
                <ChevronRightIcon
                  className="mobile-pref-control-icon mobile-pref-detail-icon size-6"
                  aria-hidden="true"
                />
              </button>
            </span>
          ) : (
            <span className="isolate inline-flex rounded-md ">
              <Button
                type="button"
                size="small"
                className="mobile-pref-nav-button dark:hover:ring-mono-500 dark:bg-mono-950 dark:ring-mono-700/50 text-mono-800 dark:text-mono-400 ring-mono-200 hover:bg-mono-50 dark:hover:border-mono-500 relative inline-flex h-8 items-center rounded-l-md rounded-r-none bg-white px-1.5 ring-0 ring-inset hover:z-10 focus:z-10 dark:hover:bg-transparent dark:hover:bg-transparent dark:hover:text-white"
                onClick={() => {
                  changeDays("-");
                }}
              >
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon
                  className="mobile-pref-control-icon mobile-pref-detail-icon"
                  aria-hidden="true"
                />
              </Button>
              <Button
                size="small"
                className="mobile-pref-nav-button dark:hover:ring-mono-500 dark:bg-mono-950 dark:ring-mono-700/50 text-mono-800 dark:text-mono-400 ring-mono-200 hover:bg-mono-50 relative -ml-px inline-flex h-8 items-center rounded-l-none rounded-r-md bg-white px-1.5 ring-0 ring-inset hover:z-10 focus:z-10 dark:hover:bg-transparent dark:hover:text-white"
                onClick={() => {
                  changeDays("+");
                }}
              >
                <span className="sr-only">Next</span>
                <ChevronRightIcon
                  className="mobile-pref-control-icon mobile-pref-detail-icon"
                  aria-hidden="true"
                />
              </Button>
            </span>
          )}
        </div>
        <div className={`group relative w-full`}>
          {isGummy ? (
            <input
              type="text"
              placeholder="Search"
              value={search}
              onInput={(e) => setSearch(e.currentTarget.value)}
              className={cn(
                "mobile-pref-command-input h-12 w-full rounded-surface-sm px-4 pr-11 text-base font-display font-black",
                "bg-white text-mono-800 dark:bg-mono-900 dark:text-mono-100",
                "placeholder:text-mono-400 dark:placeholder:text-mono-500 placeholder:font-display placeholder:font-black",
                "shadow-sunken",
                "outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40"
              )}
            />
          ) : (
            <Input
              value={search}
              onInput={(e) => setSearch(e.currentTarget.value)}
              className={`mobile-pref-command-input dark:bg-mono-950 h-8 bg-white pr-10 group-focus:opacity-100`}
            />
          )}
          {search?.length ? (
            <button
              onClick={() => setSearch("")}
              className={`absolute right-0 top-1/2 mr-2  -translate-y-1/2 opacity-30 transition-opacity focus:opacity-100 group-hover:opacity-100`}
            >
              <XMarkIcon
                className={`mobile-pref-control-icon mobile-pref-detail-icon`}
              />
            </button>
          ) : (
            <MagnifyingGlassIcon
              className={`mobile-pref-control-icon mobile-pref-detail-icon absolute right-0 top-1/2 mr-2 -translate-y-1/2 opacity-30 transition-opacity focus:opacity-100 group-hover:opacity-100`}
            />
          )}
        </div>
        <div className={`hidden sm:block`}>
          <TeamScheduleDropdown />
        </div>
      </div>
    </div>
  );
};

export default DateRangeSelector;
